import { track } from "./analytics.js";
import { renderBubble } from "./bubble.js";
import { renderModal } from "./modal.js";
import { answer, createGame, emojiGrid, nextRound, normalizeItems } from "./game.js";
import { isCooldownActive, setCooldown, updateBestStreak } from "./storage.js";
import { detectLocale, getStrings, interpolate } from "./strings.js";
import stylesText from "./styles.css";

const KEYS = {
  dismissed: "wq_dismissed",
  played: "wq_played"
};

const DAY = 24 * 60 * 60 * 1000;
const SHARE_URL = "https://howheavyisit.com";

const state = {
  config: null,
  root: null,
  host: null,
  shadow: null,
  items: [],
  game: null,
  loaded: false,
  loading: null,
  locale: "en",
  strings: null,
  liveCount: 247,
  mode: "idle",
  previousFocus: null,
  revealTimer: 0,
  chimePlayed: false
};

boot();

function boot() {
  const script = document.currentScript;
  state.locale = script?.dataset.locale || detectLocale();
  state.strings = getStrings(state.locale);
  state.config = {
    itemsUrl: script?.dataset.itemsUrl || "/widget/quiz-items.json",
    seeAllUrl: script?.dataset.seeAllUrl || "/",
    delay: Number(script?.dataset.delay || 5),
    bubbleImageUrl: script?.dataset.bubbleImageUrl || "/theme/optimized/theme/generated/how-much-does-an-elephant-weigh-cover-320w.webp",
    exitIntent: !script?.hasAttribute("data-disable-exit-intent"),
    measureType: window.location.pathname.includes("/games/") ? "digital" : "mass"
  };
  state.liveCount = 247 + Math.floor(Math.random() * 23);
  if (isCooldownActive(KEYS.dismissed)) return;
  createShadowRoot();
  if (isCooldownActive(KEYS.played)) {
    showFab("fab");
    return;
  }
  const showFromTimer = () => showBubble("timer");
  window.setTimeout(showFromTimer, Math.max(0, state.config.delay) * 1000);
  if (state.config.exitIntent && window.innerWidth >= 768) {
    document.addEventListener("mouseleave", handleExitIntent, { once: true });
  }
}

function handleExitIntent(event) {
  if (event.clientY <= 4) showBubble("exit_intent");
}

function createShadowRoot() {
  state.host = document.createElement("div");
  state.host.id = "weight-quiz-widget";
  state.shadow = state.host.attachShadow({ mode: "open" });
  const style = document.createElement("style");
  style.textContent = stylesText;
  state.root = document.createElement("div");
  state.root.className = "wq-root";
  state.shadow.append(style, state.root);
  document.body.append(state.host);
}

async function ensureGame() {
  if (state.game) return true;
  if (!state.loading) {
    state.loading = fetch(state.config.itemsUrl, { credentials: "same-origin" })
      .then((response) => {
        if (!response.ok) throw new Error(`Weight Quiz items failed: ${response.status}`);
        return response.json();
      })
      .then((items) => normalizeItems(items, state.locale));
  }
  try {
    state.items = await state.loading;
  } catch (error) {
    console.warn("[Weight Quiz]", error);
    destroy();
    return false;
  }
  if (state.items.length < 5) {
    console.warn("[Weight Quiz] quiz-items.json must contain at least 5 valid items.");
    destroy();
    return false;
  }
  state.game = createGame(state.items, state.config.measureType, state.locale);
  return Boolean(state.game?.baseline && state.game?.challenge);
}

async function showBubble(trigger) {
  if (state.mode !== "idle") return;
  if (!(await ensureGame())) return;
  state.mode = "bubble";
  track("weight_quiz_shown", { trigger });
  renderBubble(state.root, state, {
    dismiss,
    answer: (choice) => startFromBubble(choice)
  });
  playBubbleChime();
}

function playBubbleChime() {
  if (state.chimePlayed || document.hidden) return;
  state.chimePlayed = true;
  const AudioContext = window.AudioContext || window.webkitAudioContext;
  if (!AudioContext) return;
  try {
    const audio = new AudioContext();
    const play = () => {
      const now = audio.currentTime;
      const gain = audio.createGain();
      gain.gain.setValueAtTime(0.0001, now);
      gain.gain.exponentialRampToValueAtTime(0.045, now + 0.018);
      gain.gain.exponentialRampToValueAtTime(0.0001, now + 0.34);
      gain.connect(audio.destination);

      [660, 880].forEach((frequency, index) => {
        const oscillator = audio.createOscillator();
        oscillator.type = "sine";
        oscillator.frequency.setValueAtTime(frequency, now + index * 0.055);
        oscillator.connect(gain);
        oscillator.start(now + index * 0.055);
        oscillator.stop(now + 0.24 + index * 0.055);
      });

      window.setTimeout(() => audio.close().catch(() => {}), 700);
    };

    if (audio.state === "suspended") {
      audio.resume().then(play).catch(() => audio.close().catch(() => {}));
    } else {
      play();
    }
  } catch (_error) {
    // Browsers can block autoplayed audio; the quiz should remain silent rather than fail.
  }
}

function dismiss() {
  setCooldown(KEYS.dismissed, 7 * DAY);
  track("weight_quiz_dismissed");
  destroy();
}

function destroy() {
  window.clearTimeout(state.revealTimer);
  state.host?.remove();
  state.mode = "destroyed";
}

function startFromBubble(choice) {
  track("weight_quiz_started", { from: "bubble" });
  setCooldown(KEYS.played, DAY);
  state.previousFocus = state.shadow.activeElement || document.activeElement;
  submitAnswer(choice);
  showModal();
}

async function showModal(trigger = "fab") {
  if (!(await ensureGame())) return;
  if (trigger === "fab") track("weight_quiz_shown", { trigger: "fab" });
  state.mode = "modal";
  state.previousFocus = state.shadow.activeElement || document.activeElement;
  renderModal(state.root, state, modalHandlers);
  bindModalKeys();
  focusFirstAction();
  scheduleRevealAdvance();
}

function showFab(trigger = "fab") {
  state.mode = "fab";
  state.root.textContent = "";
  const fab = document.createElement("button");
  fab.className = "wq-fab";
  fab.type = "button";
  fab.setAttribute("aria-label", state.strings.aria.open);
  fab.textContent = "🏋️";
  const badge = document.createElement("span");
  badge.textContent = String(state.game?.streak || 0);
  fab.append(badge);
  fab.addEventListener("click", () => showModal(trigger));
  state.root.append(fab);
}

const modalHandlers = {
  minimize: () => {
    window.clearTimeout(state.revealTimer);
    showFab();
    restoreFocus();
  },
  answer: (choice) => {
    track("weight_quiz_started", { from: "modal" });
    setCooldown(KEYS.played, DAY);
    submitAnswer(choice);
    showModal("modal");
  },
  next: () => {
    window.clearTimeout(state.revealTimer);
    nextRound(state.game);
    showModal("modal");
  },
  share: shareResult,
  playAgain: () => {
    track("weight_quiz_play_again");
    state.game = createGame(state.items, state.config.measureType, state.locale);
    showModal("modal");
  },
  seeAll: () => track("weight_quiz_see_all")
};

function submitAnswer(choice) {
  if (!state.game || state.game.reveal || state.game.gameOver) return;
  const baselineId = state.game.baseline.id;
  const itemId = state.game.challenge.id;
  answer(state.game, choice);
  const correct = Boolean(state.game.reveal?.correct);
  track("weight_quiz_answer", {
    correct,
    streak: state.game.streak,
    itemId,
    baselineId
  });
}

function scheduleRevealAdvance() {
  window.clearTimeout(state.revealTimer);
  if (!state.game?.reveal) {
    if (state.game?.gameOver) {
      const bestStreak = updateBestStreak(state.game.streak);
      track("weight_quiz_game_over", { streak: state.game.streak, bestStreak });
    }
    return;
  }
  state.revealTimer = window.setTimeout(() => {
    nextRound(state.game);
    showModal("modal");
  }, 2500);
}

async function shareResult() {
  const emoji = emojiGrid(state.game.history, true);
  const text = interpolate(state.strings.share.template, {
    streak: state.game.streak,
    emojiGrid: emoji,
    url: SHARE_URL
  });
  track("weight_quiz_share_clicked", { streak: state.game.streak });
  if (navigator.share) {
    try {
      await navigator.share({ text });
      return;
    } catch (_error) {
      // Fall back to clipboard when native sharing is cancelled or unavailable.
    }
  }
  try {
    await navigator.clipboard.writeText(text);
    showToast();
  } catch (_error) {
    showToast(state.strings.toast.ready);
  }
}

function showToast(message = state.strings.toast.copied) {
  const toast = document.createElement("div");
  toast.className = "wq-toast";
  toast.setAttribute("role", "status");
  toast.textContent = message;
  state.root.append(toast);
  window.setTimeout(() => toast.remove(), 1800);
}

function bindModalKeys() {
  const overlay = state.root.querySelector(".wq-overlay");
  if (!overlay) return;
  overlay.addEventListener("keydown", (event) => {
    const key = event.key.toLowerCase();
    if (key === "escape") {
      event.preventDefault();
      modalHandlers.minimize();
      return;
    }
    if (!state.game.gameOver && !state.game.reveal && (key === "arrowup" || key === "h")) {
      event.preventDefault();
      modalHandlers.answer("heavier");
      return;
    }
    if (!state.game.gameOver && !state.game.reveal && (key === "arrowdown" || key === "l")) {
      event.preventDefault();
      modalHandlers.answer("lighter");
      return;
    }
    if (key === "enter") {
      event.preventDefault();
      if (state.game.gameOver) modalHandlers.playAgain();
      return;
    }
    trapFocus(event, overlay);
  });
}

function focusFirstAction() {
  const action = state.root.querySelector(".wq-actions button, .wq-primary, .wq-gameover button");
  action?.focus();
}

function trapFocus(event, container) {
  if (event.key !== "Tab") return;
  const focusable = Array.from(container.querySelectorAll("button, a[href]")).filter((node) => !node.disabled);
  if (!focusable.length) return;
  const first = focusable[0];
  const last = focusable[focusable.length - 1];
  if (event.shiftKey && state.shadow.activeElement === first) {
    event.preventDefault();
    last.focus();
  } else if (!event.shiftKey && state.shadow.activeElement === last) {
    event.preventDefault();
    first.focus();
  }
}

function restoreFocus() {
  if (state.previousFocus && typeof state.previousFocus.focus === "function") {
    state.previousFocus.focus();
  }
}
